/* Bambam Çocuk — global theme script (vanilla, no dependencies) */
(() => {
  'use strict';

  const root = document.documentElement;
  root.classList.add('js');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const routes = window.BB?.routes || { cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_url: '/cart', predictive_search_url: '/search/suggest' };
  const strings = window.BB?.strings || {};

  /* ---------------- Utilities ---------------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, wait = 250) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; };

  function toast(message) {
    let el = $('#bb-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'bb-toast'; el.className = 'toast'; el.setAttribute('role', 'status'); el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = message;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 2600);
  }
  window.BB = Object.assign(window.BB || {}, { toast });

  /* ---------------- Drawers (cart, menu, search, filters, size finder) ---------------- */
  const overlay = document.createElement('div');
  overlay.className = 'overlay';
  document.body.appendChild(overlay);
  let openDrawerEl = null;
  let lastFocus = null;

  function openDrawer(id) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    if (openDrawerEl && openDrawerEl !== drawer) closeDrawer(false);
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-open');
    document.body.classList.add('is-locked');
    openDrawerEl = drawer;
    $$(`[aria-controls="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const focusTarget = drawer.querySelector('[data-autofocus]') || drawer.querySelector('button, [href], input, select');
    setTimeout(() => focusTarget?.focus({ preventScroll: true }), 60);
  }

  function closeDrawer(restore = true) {
    if (!openDrawerEl) return;
    const id = openDrawerEl.id;
    openDrawerEl.classList.remove('is-open');
    openDrawerEl.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-open');
    document.body.classList.remove('is-locked');
    $$(`[aria-controls="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    openDrawerEl = null;
    if (restore) lastFocus?.focus?.({ preventScroll: true });
  }

  overlay.addEventListener('click', () => closeDrawer());
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawer();
    if (e.key === 'Tab' && openDrawerEl) {
      const f = $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', openDrawerEl).filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    }
  });
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); openDrawer(opener.dataset.drawerOpen); return; }
    if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); closeDrawer(); }
  });
  window.BB.openDrawer = openDrawer;
  window.BB.closeDrawer = closeDrawer;

  // The menu drawer is rendered inside the sticky header section, whose z-index makes it a stacking context: the drawer
  // painted under the body-level overlay (menu looked grey and blurred on mobile, Oct 2026). Lift it to <body>.
  function liftMenuDrawer() {
    const drawer = $('.section-header #MenuDrawer');
    if (!drawer) return;
    $$('body > #MenuDrawer').forEach((old) => old.remove());
    document.body.appendChild(drawer);
  }
  liftMenuDrawer();
  document.addEventListener('shopify:section:load', liftMenuDrawer);

  /* ---------------- Header: shrink on scroll (IntersectionObserver, no scroll listener) ---------------- */
  const header = $('[data-header]');
  const sentinel = $('#header-sentinel');
  if (header && sentinel && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => header.classList.toggle('is-scrolled', !entry.isIntersecting)).observe(sentinel);
  }

  /* ---------------- Reveal on scroll ---------------- */
  if ('IntersectionObserver' in window && !reduceMotion) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    const watch = (ctx = document) => $$('[data-reveal]:not(.is-in)', ctx).forEach((el) => io.observe(el));
    // Anything already on screen at load is shown immediately (no dependence on observer timing).
    const vh = window.innerHeight;
    $$('[data-reveal]').forEach((el) => { const r = el.getBoundingClientRect(); if (r.top < vh && r.bottom > 0) el.classList.add('is-in'); });
    watch();
    document.addEventListener('shopify:section:load', (e) => watch(e.target));
    window.BB.watchReveal = watch;
  } else {
    $$('[data-reveal]').forEach((el) => el.classList.add('is-in'));
  }

  /* ---------------- Rails: prev/next buttons ---------------- */
  function initRails(ctx = document) {
    $$('[data-rail]', ctx).forEach((rail) => {
      const track = $('.rail__track', rail);
      const prev = $('[data-rail-prev]', rail);
      const next = $('[data-rail-next]', rail);
      if (!track || !prev || !next || rail._init) return;
      rail._init = true;
      const step = () => track.clientWidth * 0.8;
      prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      const update = () => {
        prev.disabled = track.scrollLeft < 8;
        next.disabled = track.scrollLeft + track.clientWidth > track.scrollWidth - 8;
      };
      track.addEventListener('scroll', debounce(update, 60), { passive: true });
      update();
    });
  }
  initRails();
  document.addEventListener('shopify:section:load', (e) => initRails(e.target));

  /* ---------------- Cart ---------------- */
  const CART_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  function renderSections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) {
      const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const fresh = html.querySelector('#CartDrawer-Inner');
      const current = $('#CartDrawer-Inner');
      if (fresh && current) current.replaceWith(fresh);
    }
    if (sections['cart-icon-bubble']) {
      const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const fresh = html.querySelector('.cart-count');
      $$('.cart-count').forEach((el) => {
        el.replaceWith(fresh.cloneNode(true));
      });
    }
    requestAnimationFrame(() => {
      const bar = $('#CartDrawer-Inner [data-progress]');
      if (bar) bar.style.setProperty('--p', bar.dataset.progress);
    });
  }

  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: CART_SECTIONS, sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError || 'Bir hata oluştu');
    return data;
  }

  function flyToCart(sourceImg) {
    const target = $$('[data-cart-target]').find((el) => el.offsetParent !== null);
    if (!sourceImg || !target || reduceMotion) return Promise.resolve();
    const s = sourceImg.getBoundingClientRect();
    const t = target.getBoundingClientRect();
    const clone = sourceImg.cloneNode();
    clone.removeAttribute('srcset'); clone.removeAttribute('sizes'); clone.removeAttribute('loading');
    clone.src = sourceImg.currentSrc || sourceImg.src;
    Object.assign(clone.style, {
      position: 'fixed', zIndex: 120, left: `${s.left}px`, top: `${s.top}px`, width: `${s.width}px`, height: `${s.height}px`,
      objectFit: 'cover', borderRadius: '20px', pointerEvents: 'none', background: '#e8e8e8', boxShadow: '0 20px 40px -12px rgba(35,31,32,.35)',
    });
    document.body.appendChild(clone);
    const dx = t.left + t.width / 2 - (s.left + s.width / 2);
    const dy = t.top + t.height / 2 - (s.top + s.height / 2);
    const anim = clone.animate([
      { transform: 'translate(0,0) scale(1)', opacity: 1, borderRadius: '20px' },
      { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - 80}px) scale(0.45) rotate(-8deg)`, opacity: 1, offset: 0.55 },
      { transform: `translate(${dx}px, ${dy}px) scale(0.08) rotate(-14deg)`, opacity: 0.4, borderRadius: '50%' },
    ], { duration: 780, easing: 'cubic-bezier(.5,0,.2,1)' });
    return anim.finished.then(() => {
      clone.remove();
      target.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.28)' }, { transform: 'scale(0.92)' }, { transform: 'scale(1)' }], { duration: 520, easing: 'cubic-bezier(.34,1.56,.64,1)' });
    }).catch(() => clone.remove());
  }

  async function addToCart(form, button) {
    const formData = new FormData(form);
    const id = formData.get('id');
    if (!id) { toast(strings.selectSize || 'Lütfen numara seçin'); return; }
    const quantity = Number(formData.get('quantity') || 1);
    const properties = {};
    for (const [k, v] of formData.entries()) { const m = k.match(/^properties\[(.+)\]$/); if (m && v) properties[m[1]] = v; }
    button?.classList.add('is-loading');
    button?.setAttribute('aria-busy', 'true');
    try {
      const data = await cartRequest(`${routes.cart_add_url}.js`, { items: [{ id: Number(id), quantity, properties }] });
      const img = form.closest('[data-product-root]')?.querySelector('[data-fly-source], .js-fly-source') || form.closest('.card')?.querySelector('.card__img-1');
      await flyToCart(img);
      renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('bb:cart:added', { detail: data }));
      if (window.BB.cartType === 'page') { window.location.href = routes.cart_url; return; }
      openDrawer('CartDrawer');
    } catch (err) {
      toast(err.message);
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-busy');
    }
  }
  window.BB.addToCart = addToCart;

  async function addItems(items, img, button) {
    button?.classList.add('is-loading');
    try {
      const data = await cartRequest(`${routes.cart_add_url}.js`, { items });
      await flyToCart(img);
      renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('bb:cart:added', { detail: data }));
      if (window.BB.cartType === 'page') { window.location.href = routes.cart_url; return; }
      openDrawer('CartDrawer');
    } catch (err) { toast(err.message); }
    finally { button?.classList.remove('is-loading'); }
  }
  window.BB.addItems = addItems;

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    addToCart(form, e.submitter || form.querySelector('[type="submit"]'));
  });

  async function changeLine(line, quantity) {
    const inner = $('#CartDrawer-Inner');
    inner?.classList.add('is-busy');
    try {
      const data = await cartRequest(`${routes.cart_change_url}.js`, { line, quantity });
      renderSections(data.sections);
      if (document.body.dataset.template === 'cart') window.location.reload();
    } catch (err) {
      toast(err.message);
    } finally {
      $('#CartDrawer-Inner')?.classList.remove('is-busy');
    }
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (btn) {
      e.preventDefault();
      const line = Number(btn.dataset.line);
      const qty = Number(btn.dataset.qtyChange);
      changeLine(line, Math.max(0, qty));
    }
  });

  // Gift wrap toggle inside the cart drawer.
  document.addEventListener('change', async (e) => {
    const t = e.target.closest('[data-gift-toggle]');
    if (!t) return;
    const inner = $('#CartDrawer-Inner');
    inner?.classList.add('is-busy');
    try {
      const data = t.checked
        ? await cartRequest(`${routes.cart_add_url}.js`, { items: [{ id: Number(t.dataset.variant), quantity: 1 }] })
        : await cartRequest(`${routes.cart_change_url}.js`, { line: Number(t.dataset.line), quantity: 0 });
      renderSections(data.sections);
    } catch (err) { toast(err.message); t.checked = !t.checked; }
    finally { $('#CartDrawer-Inner')?.classList.remove('is-busy'); }
  });

  // Open cart drawer when the header cart is clicked (fall back to /cart without JS).
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-cart-link]');
    if (link && $('#CartDrawer') && window.BB.cartType !== 'page') { e.preventDefault(); openDrawer('CartDrawer'); }
  });

  // Animate the free-shipping progress bar on first open.
  document.addEventListener('transitionend', (e) => {
    if (e.target.id === 'CartDrawer' && e.target.classList.contains('is-open')) {
      const bar = $('#CartDrawer-Inner [data-progress]');
      if (bar) bar.style.setProperty('--p', bar.dataset.progress);
    }
  });

  /* ---------------- Quick add from product cards ---------------- */
  document.addEventListener('click', async (e) => {
    const quick = e.target.closest('[data-quick-add]');
    if (!quick) return;
    e.preventDefault();
    const handle = quick.dataset.quickAdd;
    const drawer = $('#QuickAdd');
    const body = $('#QuickAdd-Body');
    if (!drawer || !body) { window.location.href = quick.dataset.url; return; }
    body.innerHTML = '<div class="skeleton" style="aspect-ratio:4/5"></div><div class="skeleton" style="height:22px;margin-top:16px;width:70%"></div><div class="skeleton" style="height:48px;margin-top:16px"></div>';
    openDrawer('QuickAdd');
    try {
      const res = await fetch(`${quick.dataset.url}?section_id=quick-add`);
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      const content = html.querySelector('[data-quick-add-content]');
      body.innerHTML = '';
      if (content) body.appendChild(content);
      document.dispatchEvent(new CustomEvent('bb:product:loaded', { detail: { root: body } }));
    } catch {
      window.location.href = quick.dataset.url;
    }
  });

  /* ---------------- Predictive search ---------------- */
  const searchInput = $('#PredictiveSearch-Input');
  const searchResults = $('#PredictiveSearch-Results');
  if (searchInput && searchResults) {
    let controller;
    const run = debounce(async () => {
      const q = searchInput.value.trim();
      if (q.length < 2) { searchResults.innerHTML = ''; searchResults.hidden = true; $('#PredictiveSearch-Default')?.removeAttribute('hidden'); return; }
      controller?.abort();
      controller = new AbortController();
      try {
        const url = `${routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url, { signal: controller.signal });
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        const content = html.querySelector('#PredictiveSearch-Content');
        searchResults.innerHTML = content ? content.innerHTML : '';
        searchResults.hidden = false;
        $('#PredictiveSearch-Default')?.setAttribute('hidden', '');
      } catch (err) { if (err.name !== 'AbortError') searchResults.hidden = true; }
    }, 220);
    searchInput.addEventListener('input', run);
  }

  /* ---------------- Size finder (foot length -> EU size) ---------------- */
  // EU size ≈ (foot length cm + 1.5 cm allowance) × 1.5  — same table as the size guide page.
  function sizeFor(cm) { return Math.ceil(Number(((cm + 1.5) * 1.5).toFixed(3))); }
  function initSizeFinder(ctx = document) {
    $$('[data-size-finder]', ctx).forEach((el) => {
      if (el._init) return; el._init = true;
      const range = $('input[type="range"]', el);
      const out = $('[data-size-out]', el);
      const cmOut = $('[data-cm-out]', el);
      const foot = $('[data-foot]', el);
      const update = () => {
        const cm = Number(range.value);
        const size = sizeFor(cm);
        out.textContent = size;
        if (cmOut) cmOut.textContent = cm.toFixed(1).replace('.', ',');
        range.style.setProperty('--fill', `${((cm - range.min) / (range.max - range.min)) * 100}%`);
        if (foot) foot.style.setProperty('--scale', (0.55 + (cm - range.min) / (range.max - range.min) * 0.45).toFixed(3));
        el.dataset.size = size;
        $$('[data-size-link]', el).forEach((a) => {
          const base = a.dataset.base || a.getAttribute('href');
          a.dataset.base = base;
          a.href = `${base}${base.includes('?') ? '&' : '?'}filter.v.option.numara=${size}`;
        });
      };
      range.addEventListener('input', update);
      update();
    });
  }
  initSizeFinder();
  document.addEventListener('shopify:section:load', (e) => initSizeFinder(e.target));
  document.addEventListener('bb:product:loaded', (e) => initSizeFinder(e.detail.root));

  /* ---------------- Particles (hero snow / dust) ---------------- */
  $$('[data-particles]').forEach((wrap) => {
    if (reduceMotion) return;
    const count = Number(wrap.dataset.particles) || 24;
    const frag = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const p = document.createElement('i');
      p.style.setProperty('--x', `${Math.random() * 100}%`);
      p.style.setProperty('--s', `${2 + Math.random() * 5}px`);
      p.style.setProperty('--d', `${9 + Math.random() * 10}s`);
      p.style.setProperty('--delay', `${-Math.random() * 18}s`);
      p.style.setProperty('--drift', `${-40 + Math.random() * 80}px`);
      frag.appendChild(p);
    }
    wrap.appendChild(frag);
  });

  /* ---------------- Hero video: only on capable connections ---------------- */
  $$('video[data-lazy-video]').forEach((video) => {
    const conn = navigator.connection;
    const slow = conn && (conn.saveData || /2g/.test(conn.effectiveType || ''));
    const small = window.matchMedia('(max-width: 749px)').matches;
    if (slow || reduceMotion || (small && video.dataset.desktopOnly !== undefined)) return;
    $$('source[data-src]', video).forEach((s) => { s.src = s.dataset.src; });
    video.load();
    video.addEventListener('canplay', () => { video.classList.add('is-ready'); video.play().catch(() => {}); }, { once: true });
  });

  /* ---------------- Newsletter popup (once per visitor, after engagement) ---------------- */
  const popup = $('#NewsletterPopup');
  if (popup && popup.dataset.enabled === 'true') {
    let seen = false;
    try { seen = localStorage.getItem('bb_nl_seen') === '1'; } catch {}
    if (!seen && !window.Shopify?.designMode) {
      // Ad visitors were getting the cookie card and this popup within ~15 s (Oct 2026). Now it waits until the cookie
      // card is answered, the visitor has scrolled at least once, and no drawer (cart, quick add) is open.
      const delay = Number(popup.dataset.delay || 12) * 1000;
      let engaged = false;
      window.addEventListener('scroll', () => { engaged = true; }, { passive: true, once: true });
      const bannerOpen = () => {
        const b = document.getElementById('shopify-pc__banner');
        if (!b) return false;
        const cs = getComputedStyle(b);
        return cs.display !== 'none' && cs.visibility !== 'hidden' && b.getBoundingClientRect().height > 0;
      };
      const show = () => {
        if (openDrawerEl || !engaged || bannerOpen()) { setTimeout(show, 2000); return; }
        openDrawer('NewsletterPopup');
        try { localStorage.setItem('bb_nl_seen', '1'); } catch {}
      };
      setTimeout(show, delay);
    }
  }

  // After a newsletter signup the page reloads; re-open the popup so the code is visible.
  if (/customer_posted=true/.test(window.location.search) && $('#NewsletterPopup .nl-code')) {
    setTimeout(() => openDrawer('NewsletterPopup'), 300);
  }

  /* ---------------- Copy discount code ---------------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy]');
    if (!btn) return;
    try { await navigator.clipboard.writeText(btn.dataset.copy); toast(strings.copied || 'Kod kopyalandı'); } catch {}
  });

  /* ---------------- Native validation messages in the store language ---------------- */
  document.addEventListener('invalid', (e) => {
    const el = e.target;
    if (el.dataset && el.dataset.invalidMsg) el.setCustomValidity(el.validity.valueMissing ? el.dataset.invalidMsg : '');
  }, true);
  document.addEventListener('change', (e) => { if (e.target.dataset && e.target.dataset.invalidMsg) e.target.setCustomValidity(''); });
})();
